import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { formatDate } from '@/lib/finance';
import { downloadCSV } from '@/lib/csv';
import { buildExcelDocument } from '@/lib/excelExport';
import {
  listAllAccessForMerchant,
  listAccessAudit,
  listZifekClientsByIds,
  revokeDigitalAccess,
  type DigitalAccessEntry,
  type DigitalAccessAuditEntry,
  type ZifekClient,
} from '@/lib/digitalAccess';

type AccessTab = 'active' | 'history';
type TypeFilter = 'all' | 'digital' | 'physical';

const TYPE_LABEL: Record<string, string> = { digital: 'Digital', physical: 'Physique' };

function productTypeLabel(type: string | null): string {
  return TYPE_LABEL[type || 'physical'] || 'Physique';
}

/**
 * Onglet « Accès digitaux » de l'app Gestion Clients.
 * Regroupe TOUS les accès accordés (tous produits) afin de les gérer
 * ou de les retirer au même endroit, sans passer par chaque fiche produit.
 * Inclut : filtre digital/physique, export CSV et journal d'audit des retraits.
 */
export default function DigitalAccessView() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const merchantId = getCommerceId(user);

  const [tab, setTab] = useState<AccessTab>('active');
  const [access, setAccess] = useState<DigitalAccessEntry[]>([]);
  const [audit, setAudit] = useState<DigitalAccessAuditEntry[]>([]);
  const [clients, setClients] = useState<Record<number, ZifekClient>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [auditProduct, setAuditProduct] = useState('all');
  const [auditClient, setAuditClient] = useState('all');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [creatingExcel, setCreatingExcel] = useState(false);

  const load = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    setError(null);
    try {
      const [rows, history] = await Promise.all([
        listAllAccessForMerchant(merchantId),
        listAccessAudit(merchantId).catch(() => [] as DigitalAccessAuditEntry[]),
      ]);
      setAccess(rows);
      setAudit(history);
      const ids = [...rows.map((r) => r.userId), ...history.map((h) => h.userId)];
      const map: Record<number, ZifekClient> = {};
      if (ids.length > 0) {
        const list = await listZifekClientsByIds(ids);
        list.forEach((c) => { map[c.id] = c; });
      }
      setClients(map);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des accès');
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (entry: DigitalAccessEntry) => {
    setBusyId(entry.id);
    setMsg(null);
    try {
      await revokeDigitalAccess(entry.id);
      setAccess((prev) => prev.filter((a) => a.id !== entry.id));
      setMsg('Accès retiré — l\'opération a été consignée dans le journal.');
      const history = await listAccessAudit(merchantId as number).catch(() => [] as DigitalAccessAuditEntry[]);
      setAudit(history);
    } catch (err: unknown) {
      setMsg(err instanceof Error ? err.message : 'Erreur lors du retrait.');
    } finally {
      setBusyId(null);
    }
  };

  // ── Accès actifs : recherche + filtre type ────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return access.filter((a) => {
      if (typeFilter !== 'all' && (a.productType || 'physical') !== typeFilter) return false;
      if (!q) return true;
      const c = clients[a.userId];
      const name = (c?.name || '').toLowerCase();
      const email = (c?.email || '').toLowerCase();
      const product = (a.productName || '').toLowerCase();
      return name.includes(q) || email.includes(q) || product.includes(q);
    });
  }, [access, clients, search, typeFilter]);

  // ── Historique : options de filtre produit / client ───────────
  const auditProducts = useMemo(() => {
    const map = new Map<number, string>();
    audit.forEach((h) => map.set(h.productId, h.productName || `Produit #${h.productId}`));
    return Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [audit]);

  const auditClients = useMemo(() => {
    const map = new Map<number, string>();
    audit.forEach((h) => {
      const c = clients[h.userId];
      map.set(h.userId, c?.name || `Client #${h.userId}`);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [audit, clients]);

  const filteredAudit = useMemo(() => {
    return audit.filter((h) => {
      if (auditProduct !== 'all' && String(h.productId) !== auditProduct) return false;
      if (auditClient !== 'all' && String(h.userId) !== auditClient) return false;
      return true;
    });
  }, [audit, auditProduct, auditClient]);

  const stats = useMemo(() => {
    const distinctClients = new Set(access.map((a) => a.userId)).size;
    const distinctProducts = new Set(access.map((a) => a.productId)).size;
    const viaPayment = access.filter((a) => a.source === 'payment').length;
    const revoked = audit.filter((h) => h.action === 'revoke').length;
    return { total: access.length, distinctClients, distinctProducts, viaPayment, revoked };
  }, [access, audit]);

  // ── Export CSV ────────────────────────────────────────────────
  const exportActive = () => {
    const headers = ['Client', 'Email', 'Produit', 'Type', 'Source', 'Accordé le'];
    const rows = filtered.map((a) => {
      const c = clients[a.userId];
      return [
        c?.name || `Client #${a.userId}`,
        c?.email || '',
        a.productName || `Produit #${a.productId}`,
        productTypeLabel(a.productType),
        a.source === 'payment' ? 'Paiement' : 'Manuel',
        a.grantedAt ? new Date(a.grantedAt).toLocaleString('fr-FR') : '',
      ];
    });
    downloadCSV('acces-digitaux.csv', headers, rows);
  };

  const exportAudit = () => {
    const headers = ['Date', 'Action', 'Client', 'Email', 'Produit', 'Source'];
    const rows = filteredAudit.map((h) => {
      const c = clients[h.userId];
      return [
        h.createdAt ? new Date(h.createdAt).toLocaleString('fr-FR') : '',
        h.action === 'revoke' ? 'Retiré' : 'Accordé',
        c?.name || `Client #${h.userId}`,
        c?.email || '',
        h.productName || `Produit #${h.productId}`,
        h.source === 'payment' ? 'Paiement' : 'Manuel',
      ];
    });
    downloadCSV('journal-acces-digitaux.csv', headers, rows);
  };

  // ── Export Excel (fichier modifiable dans l'app Documents) ────
  const openActiveInExcel = async () => {
    if (!user || filtered.length === 0) return;
    setCreatingExcel(true);
    try {
      const headers = ['Client', 'Email', 'Produit', 'Type', 'Source', 'Accordé le'];
      const rows = filtered.map((a) => {
        const c = clients[a.userId];
        return [
          c?.name || `Client #${a.userId}`,
          c?.email || '',
          a.productName || `Produit #${a.productId}`,
          productTypeLabel(a.productType),
          a.source === 'payment' ? 'Paiement' : 'Manuel',
          a.grantedAt ? new Date(a.grantedAt).toLocaleDateString('fr-FR') : '',
        ];
      });
      await buildExcelDocument(
        user.id,
        `Accès digitaux ${new Date().toISOString().slice(0, 10)}`,
        headers,
        rows,
        { sheetName: 'Accès actifs' },
      );
      navigate('/dashboard/file-manager');
    } catch {
      /* erreur déjà gérée côté documents */
    } finally {
      setCreatingExcel(false);
    }
  };

  const openAuditInExcel = async () => {
    if (!user || filteredAudit.length === 0) return;
    setCreatingExcel(true);
    try {
      const headers = ['Date', 'Action', 'Client', 'Email', 'Produit', 'Source'];
      const rows = filteredAudit.map((h) => {
        const c = clients[h.userId];
        return [
          h.createdAt ? new Date(h.createdAt).toLocaleString('fr-FR') : '',
          h.action === 'revoke' ? 'Retiré' : 'Accordé',
          c?.name || `Client #${h.userId}`,
          c?.email || '',
          h.productName || `Produit #${h.productId}`,
          h.source === 'payment' ? 'Paiement' : 'Manuel',
        ];
      });
      await buildExcelDocument(
        user.id,
        `Journal accès digitaux ${new Date().toISOString().slice(0, 10)}`,
        headers,
        rows,
        { sheetName: 'Journal' },
      );
      navigate('/dashboard/file-manager');
    } catch {
      /* erreur déjà gérée côté documents */
    } finally {
      setCreatingExcel(false);
    }
  };

  return (
    <div>
      <div className="px-4 md:px-5 pt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-key-2-line text-secondary-600"></i>
            Accès digitaux
          </h3>
          <p className="text-xs text-foreground-500 mt-1">
            Tous les accès accordés, tous produits confondus — gérez, exportez et consultez le journal.
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un client ou un produit"
            className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 px-4 md:px-5 mt-4">
        {[
          { label: 'Accès actifs', value: stats.total, icon: 'ri-key-2-line', color: 'bg-secondary-50 text-secondary-600' },
          { label: 'Clients concernés', value: stats.distinctClients, icon: 'ri-user-line', color: 'bg-primary-50 text-primary-600' },
          { label: 'Produits digitaux', value: stats.distinctProducts, icon: 'ri-download-cloud-2-line', color: 'bg-accent-50 text-accent-600' },
          { label: 'Via paiement', value: stats.viaPayment, icon: 'ri-secure-payment-line', color: 'bg-background-200/50 text-foreground-600' },
          { label: 'Accès retirés', value: stats.revoked, icon: 'ri-arrow-go-back-line', color: 'bg-red-50 text-red-600' },
        ].map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className={`w-8 h-8 rounded-lg ${s.color} flex items-center justify-center mb-2`}>
              <i className={s.icon}></i>
            </div>
            <p className="text-xs text-foreground-500">{s.label}</p>
            <p className="text-lg font-bold text-foreground-950">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="px-4 md:px-5 mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
          {([
            { key: 'active' as AccessTab, icon: 'ri-checkbox-circle-line', label: 'Accès actifs' },
            { key: 'history' as AccessTab, icon: 'ri-history-line', label: 'Historique' },
          ]).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                tab === t.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className={`${t.icon} text-base`}></i>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'active' ? (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {([
                { key: 'all' as TypeFilter, label: 'Tous' },
                { key: 'digital' as TypeFilter, label: 'Digital' },
                { key: 'physical' as TypeFilter, label: 'Physique' },
              ]).map((f) => (
                <button
                  key={f.key}
                  onClick={() => setTypeFilter(f.key)}
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    typeFilter === f.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <button
              onClick={exportActive}
              disabled={filtered.length === 0}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-secondary-50 text-secondary-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-secondary-100 disabled:opacity-50 transition-colors cursor-pointer"
            >
              <i className="ri-file-download-line"></i>Exporter CSV
            </button>
            <button
              onClick={openActiveInExcel}
              disabled={creatingExcel || filtered.length === 0}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {creatingExcel ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-file-excel-2-line"></i>}Ouvrir dans Excel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={auditProduct}
              onChange={(e) => setAuditProduct(e.target.value)}
              className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 cursor-pointer max-w-[200px]"
            >
              <option value="all">Tous les produits</option>
              {auditProducts.map((p) => (
                <option key={p.id} value={String(p.id)}>{p.name}</option>
              ))}
            </select>
            <select
              value={auditClient}
              onChange={(e) => setAuditClient(e.target.value)}
              className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 cursor-pointer max-w-[200px]"
            >
              <option value="all">Tous les clients</option>
              {auditClients.map((c) => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
            </select>
            <button
              onClick={exportAudit}
              disabled={filteredAudit.length === 0}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-secondary-50 text-secondary-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-secondary-100 disabled:opacity-50 transition-colors cursor-pointer"
            >
              <i className="ri-file-download-line"></i>Exporter CSV
            </button>
            <button
              onClick={openAuditInExcel}
              disabled={creatingExcel || filteredAudit.length === 0}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {creatingExcel ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-file-excel-2-line"></i>}Ouvrir dans Excel
            </button>
          </div>
        )}
      </div>

      {msg && (
        <div className="mx-4 md:mx-5 mt-4 px-4 py-2.5 rounded-lg bg-secondary-50 text-secondary-800 text-sm">{msg}</div>
      )}

      <div className="p-4 md:p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center py-20">
            <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
            <p className="text-foreground-600 mb-3 text-sm">{error}</p>
            <button onClick={load} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors">
              Réessayer
            </button>
          </div>
        ) : tab === 'active' ? (
          filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-background-200/70 rounded-lg">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-key-2-line text-2xl text-foreground-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-800 mb-1">Aucun accès</h3>
              <p className="text-sm text-foreground-500 text-center max-w-sm">
                Aucun accès ne correspond à ce filtre. Les accès apparaissent ici dès que vous les accordez.
              </p>
            </div>
          ) : (
            <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-background-200/70 bg-background-100 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                <div className="col-span-4">Client</div>
                <div className="col-span-4">Produit</div>
                <div className="col-span-1">Type</div>
                <div className="col-span-1">Source</div>
                <div className="col-span-1">Date</div>
                <div className="col-span-1 text-center">Action</div>
              </div>
              <div className="divide-y divide-background-200/70">
                {filtered.map((entry) => {
                  const c = clients[entry.userId];
                  return (
                    <div key={entry.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 items-center hover:bg-background-50/50 transition-colors">
                      <div className="md:col-span-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {c?.image ? (
                              <img src={c.image} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <i className="ri-user-line text-accent-700 text-sm"></i>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground-900 truncate">{c?.name || `Client #${entry.userId}`}</p>
                            <p className="text-xs text-foreground-400 truncate">{c?.email || (c?.userName ? `@${c.userName}` : '')}</p>
                          </div>
                        </div>
                      </div>
                      <div className="md:col-span-4 min-w-0">
                        <p className="text-sm text-foreground-800 truncate">{entry.productName || `Produit #${entry.productId}`}</p>
                        <button
                          onClick={() => navigate(`/dashboard/products/${entry.productId}/edit`)}
                          className="text-xs text-primary-600 hover:text-primary-700 cursor-pointer inline-flex items-center gap-1"
                        >
                          <i className="ri-external-link-line"></i>Voir le produit
                        </button>
                      </div>
                      <div className="md:col-span-1">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          (entry.productType || 'physical') === 'digital' ? 'bg-accent-50 text-accent-700' : 'bg-background-100 text-foreground-600'
                        }`}>
                          <i className={(entry.productType || 'physical') === 'digital' ? 'ri-download-cloud-2-line' : 'ri-box-3-line'}></i>
                          {productTypeLabel(entry.productType)}
                        </span>
                      </div>
                      <div className="md:col-span-1">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          entry.source === 'payment' ? 'bg-primary-50 text-primary-700' : 'bg-secondary-50 text-secondary-700'
                        }`}>
                          <i className={entry.source === 'payment' ? 'ri-secure-payment-line' : 'ri-hand-heart-line'}></i>
                          {entry.source === 'payment' ? 'Paiement' : 'Manuel'}
                        </span>
                      </div>
                      <div className="md:col-span-1">
                        <span className="text-xs text-foreground-400 whitespace-nowrap">{formatDate(entry.grantedAt)}</span>
                      </div>
                      <div className="md:col-span-1 flex md:justify-center">
                        <button
                          onClick={() => handleRevoke(entry)}
                          disabled={busyId === entry.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                          title="Retirer l'accès"
                        >
                          {busyId === entry.id ? (
                            <i className="ri-loader-4-line animate-spin"></i>
                          ) : (
                            <i className="ri-delete-bin-line"></i>
                          )}
                          Retirer
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )
        ) : (
          /* ── Historique (journal d'audit) ── */
          filteredAudit.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-background-200/70 rounded-lg">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-history-line text-2xl text-foreground-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-800 mb-1">Aucun historique</h3>
              <p className="text-sm text-foreground-500 text-center max-w-sm">
                Les accès accordés et retirés apparaîtront ici, filtrables par produit et par client.
              </p>
            </div>
          ) : (
            <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-background-200/70 bg-background-100 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                <div className="col-span-2">Date</div>
                <div className="col-span-2">Action</div>
                <div className="col-span-4">Client</div>
                <div className="col-span-3">Produit</div>
                <div className="col-span-1">Source</div>
              </div>
              <div className="divide-y divide-background-200/70">
                {filteredAudit.map((h) => {
                  const c = clients[h.userId];
                  const revoked = h.action === 'revoke';
                  return (
                    <div key={h.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-3.5 items-center hover:bg-background-50/50 transition-colors">
                      <div className="md:col-span-2">
                        <span className="text-xs text-foreground-500 whitespace-nowrap">
                          {h.createdAt ? new Date(h.createdAt).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </span>
                      </div>
                      <div className="md:col-span-2">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          revoked ? 'bg-red-50 text-red-600' : 'bg-primary-50 text-primary-700'
                        }`}>
                          <i className={revoked ? 'ri-arrow-go-back-line' : 'ri-check-double-line'}></i>
                          {revoked ? 'Retiré' : 'Accordé'}
                        </span>
                      </div>
                      <div className="md:col-span-4 min-w-0">
                        <p className="text-sm font-medium text-foreground-800 truncate">{c?.name || `Client #${h.userId}`}</p>
                        <p className="text-xs text-foreground-400 truncate">{c?.email || (c?.userName ? `@${c.userName}` : '')}</p>
                      </div>
                      <div className="md:col-span-3 min-w-0">
                        <p className="text-sm text-foreground-700 truncate">{h.productName || `Produit #${h.productId}`}</p>
                      </div>
                      <div className="md:col-span-1">
                        <span className="text-xs text-foreground-500 whitespace-nowrap">
                          {h.source === 'payment' ? 'Paiement' : 'Manuel'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )}

        {!loading && !error && (
          <div className="flex items-center justify-between mt-4 text-sm text-foreground-500">
            <span>
              {tab === 'active'
                ? `${filtered.length} accès affiché${filtered.length > 1 ? 's' : ''}`
                : `${filteredAudit.length} entrée${filteredAudit.length > 1 ? 's' : ''} dans le journal`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}